import React, { useEffect, useState } from "react";
import PicData from "./entity/PicData";
import { UnderScoreLanguageCodeNormalizer } from "./util";
import { PICEvents } from "./enum/Events";
import Loading from "./loading";

interface MicroFrontendProps {
  name: string;
  cloudFrontUrl: any;
  history?: any;
  data?: PicData;
}


const MicroFrontend = ({name, cloudFrontUrl, history, data}: MicroFrontendProps) => {
  const containerId = `${name}-container`;
  const scriptId = `micro-frontend-script-${name}`;
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (document.getElementById(scriptId)) {
      window.addEventListener(PICEvents.LoadedModule, handleLoadedPICModule);
      sendRenderModuleNotification(name, containerId, data, history);
      return () => {
        window.removeEventListener(PICEvents.LoadedModule, handleLoadedPICModule);
        sendUnmountModuleNotification(name, containerId);
      };
    }

    fetch(`${cloudFrontUrl}/asset-manifest.json`, { credentials: "include" })
      .then((res) => {
        if (res.ok) {
          return res.json();
        } else {
          throw new Error(
            `Assets can not be fetched, response: ${JSON.stringify(res)}`
          );
        }
      })
      .then(({ entrypoints }) => {
        const injectLibFromStack = function () {
          if (entrypoints.length > 0) {
            // assuming the expected load sequence matches the order in the entrypoints array,
            // only add the script id and attach the onload event to the last script file
            // so the event gets sent after all prerequisite scripts finish loading
            const nextLib = entrypoints.shift()
            const headTag = document.querySelector('head')

            const script = document.createElement('script')
            script.src = `${cloudFrontUrl}/${nextLib}`
            script.crossOrigin = 'use-credentials'

            script.onload = (e) => {
              if (entrypoints.length === 0) {
                script.id = scriptId
                window.addEventListener(PICEvents.LoadedModule, handleLoadedPICModule);
                sendRenderModuleNotification(name, containerId, data, history)
              }

              injectLibFromStack()
            }

            headTag.appendChild(script)
          }
        };
        injectLibFromStack();
      })
      .catch((error) => {
        console.error(error);
      });

    return () => {
      window.removeEventListener(PICEvents.LoadedModule, handleLoadedPICModule);
      sendUnmountModuleNotification(name, containerId);
    };
  }, []);

  useEffect(() => {
    const hyphenatedLanguageCode = new UnderScoreLanguageCodeNormalizer(data.languageCode).getNormalizerCode();
    sendUpdatePropNotification(hyphenatedLanguageCode, "changeLanguage");
  },[data.languageCode]);

  /**
  * Set isLoading to false to remove spinner when loadedPICModule event is listened
  */
  function handleLoadedPICModule() {
	  setIsLoading(false);
  }


  return (
    <>
      {isLoading && <Loading />}
      <main id={containerId} />
    </>
  );
};

/**
 * Sends the notification when prop is updated.
 */
function sendUpdatePropNotification(prop: any, eventName: string) {

  const eventData = { detail: prop };

  const updatePropEvent = new CustomEvent(eventName, eventData);
  window.dispatchEvent(updatePropEvent);
}

/**
 * Sends the notification after the JS is loaded successfully to start the module rendering.
 */
function sendRenderModuleNotification(moduleName: string,
                                      containerId: string,
                                      data?: PicData,
                                      history?: any) {
  const eventName = `render${moduleName}`;
  const hyphenatedLanguageCode = new UnderScoreLanguageCodeNormalizer(data.languageCode).getNormalizerCode();
  const updatedData = {
    ...data,
    languageCode: hyphenatedLanguageCode
  }
  const eventData = { containerId, history,  data: updatedData} ;

  // Create and send JS load notification for load event
  const jsLoadNotificationEvent = new CustomEvent(eventName, { detail: eventData });
  window.dispatchEvent(jsLoadNotificationEvent);
}

/**
 * Sends an event when the component is being unmounted to start the clean up process
 * on module.
 */
function sendUnmountModuleNotification(moduleName: string,
                                       containerId: string) {
  const eventName = `unmount${moduleName}`;
  const eventData = { containerId };

  // Create and send unmount notification event
  const moduleUnmountNotificationEvent = new CustomEvent(eventName, { detail: eventData });
  window.dispatchEvent(moduleUnmountNotificationEvent);
}

MicroFrontend.defaultProps = {
  document,
  window,
};

export default MicroFrontend;
